import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { useTableSort } from '@/lib/use-table-sort';
import { errorMessage } from '@/lib/api';
import {
  CONTRACT_LIST_FILTER,
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUS_TONES,
  contractPaths,
  listSalesContracts,
  type ContractListFilter,
  type SalesContractRow,
} from '@/lib/contracts';
import { formatShortDate } from '@/proposals/lib/money';

/** The filter tabs, in the order they show. */
const FILTERS: readonly { readonly key: ContractListFilter; readonly label: string }[] = [
  { key: CONTRACT_LIST_FILTER.open, label: 'Open' },
  { key: CONTRACT_LIST_FILTER.signed, label: 'Signed' },
  { key: CONTRACT_LIST_FILTER.closed, label: 'Declined or voided' },
  { key: CONTRACT_LIST_FILTER.all, label: 'All' },
];

/** What an empty tab says. */
const EMPTY_MESSAGES: Readonly<Record<ContractListFilter, string>> = {
  [CONTRACT_LIST_FILTER.open]: 'Contracts being drafted, reviewed by the client, or out for signature show up here.',
  [CONTRACT_LIST_FILTER.signed]: 'Signed contracts show up here.',
  [CONTRACT_LIST_FILTER.closed]: 'Contracts the client declined or that were voided show up here.',
  [CONTRACT_LIST_FILTER.all]: 'Contracts are created from an event’s Contracts tab. Every one shows up here.',
};

const FILTER_PARAM = 'filter';

function isFilter(value: string | null): value is ContractListFilter {
  return FILTERS.some((filter) => filter.key === value);
}

/** Sortable columns. */
const CONTRACT_SORT_FIELDS = {
  name: 'name',
  client: 'client',
  eventDate: 'eventDate',
  status: 'statusLabel',
  updated: 'updatedAt',
} as const;
const CONTRACT_SORT_KEYS = Object.values(CONTRACT_SORT_FIELDS);
const DEFAULT_SORT = { key: CONTRACT_SORT_FIELDS.updated, direction: 'desc' as const };

/** A list row with the fields flattened so `sortRows` can order by them. */
interface ContractTableRow extends SalesContractRow {
  readonly client: string;
  readonly statusLabel: string;
}

/** What the search box matches: contract name and number, client, event and status. */
const SEARCH_FIELDS: ReadonlyArray<keyof ContractTableRow> = ['name', 'contractNumber', 'client', 'eventName', 'statusLabel'];

function toTableRow(row: SalesContractRow): ContractTableRow {
  return { ...row, client: row.accountName ?? '', statusLabel: CONTRACT_STATUS_LABELS[row.status] };
}

/**
 * Sales → Contracts: every event contract and where it stands, so sales can
 * chase the ones still in motion without opening each event. Each row opens
 * the event's Contracts tab, where the contract's actions live. Gated by the
 * `manage_contracts` permission at the route.
 *
 * @returns The contracts list.
 */
export function ContractsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const requested = params.get(FILTER_PARAM);
  const filter: ContractListFilter = isFilter(requested) ? requested : CONTRACT_LIST_FILTER.open;
  const { sort, onSort } = useTableSort(CONTRACT_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();

  const contracts = useQuery({
    queryKey: ['sales-contracts', filter],
    queryFn: ({ signal }) => listSalesContracts(filter, signal),
  });

  const allRows = useMemo(() => (contracts.data ?? []).map(toTableRow), [contracts.data]);
  const rows = useMemo(() => sortRows(filterRows(allRows, search, SEARCH_FIELDS), sort), [allRows, search, sort]);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="Contracts"
        subtitle="Every event contract and where it stands, from draft to signed."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search contracts"
          placeholder="Contract #, client, event or status"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <SegmentedControl<ContractListFilter>
          label="Show"
          value={filter}
          onChange={(next) => {
            const nextParams = new URLSearchParams(params);
            nextParams.set(FILTER_PARAM, next);
            setParams(nextParams, { replace: true });
          }}
          options={FILTERS.map((f) => ({ key: f.key, label: f.label }))}
        />
      </div>

      {contracts.isPending ? (
        <SkeletonTable />
      ) : contracts.isError ? (
        <ErrorNotice title="Contracts unavailable" message={errorMessage(contracts.error)} />
      ) : rows.length === 0 ? (
        <Panel>
          {allRows.length > 0 ? (
            <EmptyState title="No contracts match" message="Try a different search, or another tab." />
          ) : (
            <EmptyState title="No contracts here" message={EMPTY_MESSAGES[filter]} />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Contract', sortKey: CONTRACT_SORT_FIELDS.name },
            { label: 'Client', sortKey: CONTRACT_SORT_FIELDS.client },
            { label: 'Event date', sortKey: CONTRACT_SORT_FIELDS.eventDate },
            { label: 'Status', sortKey: CONTRACT_SORT_FIELDS.status },
            { label: 'Updated', sortKey: CONTRACT_SORT_FIELDS.updated },
          ]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <Link
                  to={contractPaths({ kind: 'event', id: row.eventId }).back}
                  className="font-bold text-primary hover:underline"
                >
                  {row.name}
                </Link>
                {row.contractNumber ? (
                  <span className="block text-portal-micro text-portal-muted">#{row.contractNumber}</span>
                ) : null}
              </td>
              <td className="p-3 text-portal-small text-portal-muted">
                {row.client || '—'}
                {row.eventCount > 1 ? (
                  <span className="block text-portal-micro">Series of {row.eventCount} events</span>
                ) : null}
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                {formatShortDate(row.eventDate)}
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={CONTRACT_STATUS_TONES[row.status]}>{row.statusLabel}</Pill>
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                {formatShortDate(row.updatedAt)}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}
